// src/elements/product/ProductChildren.js
import React, { useEffect, useState } from "react";
import Layout from "../../common/Layout";
import SEO from "../../common/SEO";
import { useParams, Link } from "react-router-dom";
import { productsAPI } from "../../utils/api";
import { addChildToParent, updateNodeById, deleteNodeById } from "../../utils/treeUtils";

// A simple form to add a child
const AddChildForm = ({ onAdd, parentId, autoFocus = false }) => {
  const [title, setTitle] = useState("");
  const [itemNo, setItemNo] = useState("");
  const [type, setType] = useState("");

  const submit = (e) => {
    e && e.preventDefault();
    if (!title.trim()) return;
    const child = {
      id: Date.now() + Math.floor(Math.random() * 1000), // temp id, backend should return real id
      title: title.trim(),
      itemNo: itemNo.trim(),
      type: type.trim(),
      children: []
    };
    onAdd(parentId, child);
    setTitle("");
    setItemNo("");
    setType("");
  };

  return (
    <form className="add-child-form" onSubmit={submit}>
      <input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} autoFocus={autoFocus} />
      <input placeholder="Item no" value={itemNo} onChange={e => setItemNo(e.target.value)} />
      <input placeholder="Type" value={type} onChange={e => setType(e.target.value)} />
      <button type="submit">Add</button>
    </form>
  );
};

// Recursive node component
const ChildNode = ({ node, onAddChild, onUpdateNode, onDeleteNode }) => {
  const [expanded, setExpanded] = useState(false);

  const handleAdd = (parentId, child) => {
    onAddChild(parentId, child);
    setExpanded(true);
  };

  return (
    <div className="node-wrapper">
      <div className="node-header">
        <div>
          <strong>{node.title}</strong>
          <div className="node-meta">{node.type ? `${node.type}` : ""} {node.itemNo ? ` • ${node.itemNo}` : ""}</div>
        </div>
        <div className="node-actions">
          <button onClick={() => setExpanded(s => !s)}>{expanded ? "Collapse" : "Expand"}</button>
          <button onClick={() => onDeleteNode(node.id)} style={{ marginLeft: 8 }}>Delete</button>
        </div>
      </div>

      {expanded && (
        <div className="node-children">
          <AddChildForm onAdd={handleAdd} parentId={node.id} />
          <div className="children-list">
            {(node.children || []).map(child => (
              <ChildNode
                key={child.id}
                node={child}
                onAddChild={onAddChild}
                onUpdateNode={onUpdateNode}
                onDeleteNode={onDeleteNode}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ProductChildren = () => {
  const { id } = useParams();
  const pid = parseInt(id, 10);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // fetch product
  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await productsAPI.getById(pid);
        if (!mounted) return;
        setProduct(data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch product");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => (mounted = false);
  }, [pid]);

  const persist = async (updatedProduct) => {
    setProduct(updatedProduct);
    try {
      await productsAPI.update(updatedProduct.id, updatedProduct);
    } catch (err) {
      console.error("Persist failed", err);
      // keep optimistic local changes; show UI notification in real app
    }
  };

  // Add child anywhere (parentId may be product id or node id)
  const handleAddChild = async (parentId, childObj) => {
    // optimistic local update
    const updated = addChildToParent(product, parentId, childObj);
    await persist(updated);

    // Try to persist using API addChild to receive server id (if supported)
    try {
      const created = await productsAPI.addChild(product.id, parentId, childObj);
      // replace temp id with server id
      const updatedWithId = updateNodeById(updated, { ...childObj, id: created.id, children: [] });
      await persist(updatedWithId);
    } catch (err) {
      // If API doesn't provide addChild endpoint, the update above already persisted full product via update()
      // In this mock, both paths work.
      console.warn("API addChild failed or not implemented", err);
    }
  };

  const handleDeleteNode = async (nodeId) => {
    const updated = deleteNodeById(product, nodeId);
    if (!updated) {
      // product root was deleted (unlikely) -> keep product but empty children
      await persist({ ...product, children: [] });
    } else {
      await persist(updated);
    }
  };

  if (loading) {
    return <Layout><div style={{ padding: 40, textAlign: "center" }}>Loading product...</div></Layout>;
  }
  if (!product) return <Layout><div style={{ padding: 40, textAlign: "center" }}>Product not found</div></Layout>;

  return (
    <>
      <SEO title={`${product.title} — Children`} />
      <Layout>
        <div style={{ padding: 24 }}>
          <div style={{ marginBottom: 12 }}>
            <Link to={`${(process.env.PUBLIC_URL || "").replace(/\/$/, "")}/products`}>← Back to products</Link>
          </div>

          <h1>{product.title} — Children</h1>
          {error && <div style={{ color: "red" }}>{error}</div>}

          <section style={{ marginTop: 18 }}>
            <h3>Root level</h3>
            <AddChildForm onAdd={handleAddChild} parentId={product.id} autoFocus />
            <div style={{ marginTop: 12 }}>
              {(product.children || []).length === 0 && <div>No children yet</div>}
              {(product.children || []).map(child => (
                <ChildNode
                  key={child.id}
                  node={child}
                  onAddChild={handleAddChild}
                  onUpdateNode={(updatedNode) => {
                    // write-back updated node into product object
                    const newProduct = updateNodeById(product, updatedNode);
                    persist(newProduct);
                  }}
                  onDeleteNode={handleDeleteNode}
                />
              ))}
            </div>
          </section>

          {/* Example controls: you can add UI to move nodes */}
          <section style={{ marginTop: 24 }}>
            <h4>Utilities (developer)</h4>
            <small>Move / advanced operations can be wired to UI here (example calls in code).</small>
          </section>
        </div>
      </Layout>
    </>
  );
};

export default ProductChildren;
